'use client';

import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';
import { createManufacture, updateManufacture, getManufactureById } from '../../../../stores/manufactureStore';
import { formatUpperCase } from '../../../../utils/formatTitleCase';
import PickerField from '../../../../components/PickerField';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useCompanyDataList } from '../../../../stores/companyStore';

const ManufactureAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    name: '',
    description: '',
    company: typeof window !== 'undefined' ? localStorage.getItem('companyId') || '' : ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getManufactureById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues((prev) => ({
            ...prev,
            name: data.name ?? prev.name,
            description: data.description ?? prev.description,
            company: data.company_id ?? prev.company
          }));
        }
      } catch (err) {
        console.error('Failed to load manufacture:', err);
        notify({ message: t('manufacture.failedLoadData', 'Failed to load manufacture data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('manufacture.validationNameRequired', 'Manufacture Name is required')),
    company: Yup.string().required(t('manufacture.validationCompanyRequired', 'Company is required'))
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: formatUpperCase(values.name) ?? '',
          description: values.description ?? '',
          company: Number(values.company)
        };

        if (id) {
          await updateManufacture(id, payload);
          notify({ message: t('manufacture.updateSuccess', 'Manufacture updated successfully'), severity: 'success' });
        } else {
          await createManufacture(payload);
          notify({ message: t('manufacture.createSuccess', 'Manufacture added successfully'), severity: 'success' });
        }

        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('manufacture.title', 'Manufacture')
          : t('general.add', 'Add') + ' ' + t('manufacture.title', 'Manufacture')
      }
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('masterCompany.title', 'Company') + ' *'}
                  name="company"
                  value={formik.values.company}
                  onChange={(val) => formik.setFieldValue('company', val)}
                  onBlur={() => formik.setFieldTouched('company', true)}
                  error={formik.touched.company && Boolean(formik.errors.company)}
                  helperText={formik.touched.company && formik.errors.company}
                  {...companyPickerParams}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('manufacture.name', 'Manufacture Name') + ' *'}
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    formik.setFieldValue('name', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('manufacture.description', 'Description')}
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default ManufactureAddEdit;
